import { useState, useRef, useEffect, useContext } from 'react'
import { Box, Chip, CircularProgress, Typography } from '@mui/material'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'

import { LinksContext } from '../../../context/LinkProvider'
import { MediaContext } from '../../../context/MediaProvider'
import ProfilePreviewCard from './ProfilePreviewCard'

export function VideoPagePreview({ userData }) {
    const videoRef = useRef(null)
    const [onMute, setOnMute] = useState(true)
    const [videoError, setVideoError] = useState(false)
    const { linkButtons, socialLinks } = useContext(LinksContext)
    const { videoSrc } = useContext(MediaContext)

    // A new upload changes videoSrc — clear any prior load error so the new video is re-attempted
    // (otherwise the UI stays stuck on the broken-image fallback).
    useEffect(() => setVideoError(false), [videoSrc])

    const toggleMute = () => setOnMute(!onMute)

    const styles = {
        container: {
            m: 0,
            p: 0,
            display: 'flex',
            flexDirection: 'row-reverse',
            justifyContent: 'space-evenly',
            gap: 1.2,
        },
        chip: {
            color: 'primary.bg',
            backgroundColor: 'primary.main',
            '& .MuiChip-icon': { color: 'primary.bg' },
            ':hover': { backgroundColor: '#FF0271' },
        },
        videoWrapper: {
            m: 0,
            p: 0,
            height: '82vh',
            width: '18.4vw',
            overflow: 'hidden',
            border: '11px solid #000',
            borderRadius: '32px',
            position: 'relative',
        },
        video: {
            width: '100%',
            height: '82vh',
            objectFit: 'cover',
        },
        fallbackContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        fallbackText: {
            fontFamily: 'inherit',
            fontWeight: 500,
            margin: '1rem 0',
            textAlign: 'center',
        },
    }

    return (
        <Box sx={styles.container}>
            {!videoError && (
                <Chip
                    label="Video"
                    onClick={toggleMute}
                    icon={onMute ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    sx={styles.chip}
                />
            )}

            <Box sx={styles.videoWrapper}>
                {videoSrc ? (
                    <>
                        {!videoError ? (
                            <video
                                src={videoSrc}
                                autoPlay
                                playsInline
                                loop
                                ref={videoRef}
                                muted={onMute}
                                style={styles.video}
                                onError={() => setVideoError(true)}
                            />
                        ) : (
                            <img
                                src={videoSrc}
                                alt=""
                                style={styles.video}
                            />
                        )}
                        <ProfilePreviewCard
                            socials={socialLinks}
                            linkButtons={linkButtons}
                            userData={userData}
                        />
                    </>
                ) : (
                    <Box sx={styles.fallbackContainer}>
                        {videoRef ? (
                            <Typography variant="body" component="p" sx={styles.fallbackText}>
                                Upload a video to view it in preview.
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="body" component="p" sx={styles.fallbackText}>
                                    Loading preview
                                </Typography>
                                <CircularProgress />
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
